import { Check, Lock } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';
import {
  PASSWORD_RULE_KEYS,
  checkPasswordPolicy,
  type PasswordRuleKey,
} from '@shared/lib/password-policy';

/** i18n key for each rule's label, under `auth.newPassword.policy.*`. */
const RULE_I18N_KEY: Record<PasswordRuleKey, string> = {
  minLength: 'minLength',
  mixedCase: 'mixedCase',
  number: 'number',
  special: 'special',
  notEmail: 'notEmail',
  latinOnly: 'latinOnly',
};

/**
 * The "Password must" card (DEV-3110). Shows each policy rule with a live
 * green check once satisfied as the user types. Drives — and is driven by —
 * the same {@link checkPasswordPolicy} used by the form's zod schema.
 */
export function PasswordChecklist({
  password,
  email,
  className,
}: {
  password: string;
  email?: string | null;
  className?: string;
}) {
  const { t } = useTranslation('auth');
  const result = checkPasswordPolicy(password, email);

  return (
    <div className={cn('rounded-lg border border-input bg-background p-3', className)}>
      <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
        <Lock className="size-4" aria-hidden />
        {t('newPassword.policy.title', 'Password must')}
      </div>
      <ul className="space-y-1.5">
        {PASSWORD_RULE_KEYS.map((key) => {
          const passed = result[key];
          return (
            <li
              key={key}
              className={cn(
                'flex items-center gap-2 text-[13px] transition-colors',
                passed ? 'text-emerald-600 dark:text-emerald-500' : 'text-muted-foreground',
              )}
            >
              <span
                className={cn(
                  'flex size-4 shrink-0 items-center justify-center rounded-full border',
                  passed
                    ? 'border-emerald-600 bg-emerald-600 text-white dark:border-emerald-500 dark:bg-emerald-500'
                    : 'border-muted-foreground/40',
                )}
                aria-hidden
              >
                {passed && <Check className="size-2.5" strokeWidth={3} />}
              </span>
              {t(`newPassword.policy.${RULE_I18N_KEY[key]}`)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
